import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
    ActionType,
    CaasMondayWebformsComponent,
    ContextFormElement,
    FormSubmitEvent,
    HeaderConfigService,
    MondayWebformsFormControl,
    TrackingService,
    WebformsAction,
    hasToBeTrue,
} from '@os-components';
import { SewDialog, SewHeadline, SewWsButton } from '@sew/ui-components-angular/standalone';
import { Observer } from 'rxjs';
import { CaasMondayWebformsModule } from '../../../../../lib-os-components/src/lib/components/caas-monday-webforms/caas-monday-webforms.module';
import { CaasMondayWebformsService } from '../../../../../lib-os-components/src/lib/components/caas-monday-webforms/caas-monday-webforms.service';
import { tracking } from '../../helpers/tracking';
import { BlogLabels } from '../../models/label.model';
import {
    FormData,
    MarketoFormData,
    SewMailFormData,
    WebformsService,
} from '../../services/webforms/webforms.service';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const MktoForms2: any;

export interface MarketoFormValues {
    salutation: string;
    surname: string;
    lastname: string;
    mail: string;
    acceptTerms: boolean;
}

@Component({
    selector: 'blog-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    imports: [CommonModule, CaasMondayWebformsModule, SewHeadline, SewDialog, SewWsButton],
})
export class FormComponent {
    @Input() public darkTheme = false;
    @Input() public labels?: BlogLabels;

    public title = '';
    public mac: string | null = null;
    public displaySuccessDialog = false;
    public displayErrorDialog = false;
    public action: WebformsAction | null = null;
    public ActionType = ActionType;

    public constructor(
        private webformsService: WebformsService,
        private caasMondayWebformsService: CaasMondayWebformsService,
        private trackingService: TrackingService,
        public sewConfigService: HeaderConfigService
    ) {}

    public onSubmit(formSubmitEvent: FormSubmitEvent) {
        this.trackingService.trackEvent(tracking.step.subscribe.formSubscribeButtonClicked);
        const subscribeObserver: Partial<Observer<unknown>> = {
            complete: () => {
                this.displaySuccessDialog = true;
            },
            error: (error: HttpErrorResponse) => {
                switch (error.status) {
                    // captcha error
                    case 498:
                        this.caasMondayWebformsService.getCaptcha(Math.floor(Math.random() * 9999999) + 1);
                        console.error('wrong captcha');
                        break;
                    default:
                        console.error('There was a problem while subscribing');
                        this.displayErrorDialog = true;
                        break;
                }
            },
        };
        const form = formSubmitEvent.form;
        const formData = this.getFormData(form);
        if (this.action === null) {
            return;
        }

        const property = this.action.property;
        switch (this.action.type) {
            case ActionType.MARKETO_ACTION:
                for (const key in formData) {
                    if (Object.prototype.hasOwnProperty.call(formData, key)) {
                        const value = formData[key];
                        if (typeof value === 'boolean') {
                            formData[key] = String(value);
                        }
                    }
                }
                // eslint-disable-next-line no-case-declarations
                const marketoBody: MarketoFormData = {
                    marketoFormId: Array.isArray(property)
                        ? property[0].value.toString()
                        : property.value.toString(),
                    formData,
                };
                this.webformsService
                    .subscribeWithMarketo(marketoBody as MarketoFormData)
                    .subscribe(subscribeObserver);
                break;
            case ActionType.SEW_MAIL_ACTION:
                if (!this.mac) {
                    return;
                }
                if (!Array.isArray(property)) {
                    return;
                }
                // eslint-disable-next-line no-case-declarations
                const sewMailBody: SewMailFormData = { formData };
                property.forEach((prop) => {
                    sewMailBody[prop.name] = prop.value;
                });
                sewMailBody.mac = this.mac;
                this.webformsService.subscribeWithSewMail(sewMailBody).subscribe(subscribeObserver);
                break;
            default:
                // eslint-disable-next-line no-case-declarations
                const exhaustiveCheck: never = this.action.type;
                throw new Error(`Unkown action type: ${exhaustiveCheck}`);
        }
        form.reset();
    }

    public createFormControl = (
        formElement: ContextFormElement,
        value: string | null,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _caasMondayWebforms: CaasMondayWebformsComponent
    ) => {
        const control = new MondayWebformsFormControl(
            value,
            formElement.validators,
            undefined,
            formElement.label,
            true
        );
        if (formElement.name === 'requestOptIn') {
            const currentLanguage = this.sewConfigService.getLanguage().toLowerCase();

            if (formElement.elements !== undefined && formElement.elements?.length > 0) {
                formElement.elements.forEach((element) => {
                    const matches = element.label.match(/\{([^}]*)}/gm);

                    if (matches === null || matches.length !== 2) {
                        return;
                    }

                    let index = 0;
                    element.label = element.label.replace(
                        matches[index],
                        `<a href="/${currentLanguage}/technical_pages/forwarding/forwarding_data_protection_info.html">${matches[
                            index
                        ].substring(1, matches[index].length - 1)}</a>`
                    );
                    index = 1;
                    element.label = element.label.replace(
                        matches[index],
                        `<a href="/${currentLanguage}/technical_pages/forwarding/forwarding_declaration_of_consent.html">${matches[
                            index
                        ].substring(1, matches[index].length - 1)}</a>`
                    );
                });
                control.addValidators(hasToBeTrue());
            }
        }

        return control;
    };

    public onGetTitle(val: string) {
        this.title = val;
    }

    public onGetMac(val: string) {
        this.mac = val;
    }

    public setActionType(value: WebformsAction | null) {
        this.action = value;
    }

    public closeDialog() {
        this.displayErrorDialog = false;
        this.displaySuccessDialog = false;
    }

    private getFormData(form: UntypedFormGroup) {
        const formData: FormData = {};
        Object.keys(form.value).forEach((groupKey) => {
            Object.keys(form.value[groupKey]).forEach((key) => {
                const value = form.value[groupKey][key];
                formData[key] = value;
            });
        });

        return formData;
    }
}
